export function fullName(user) {
  if (!user) return 'Unknown user';
  return `${user.firstName} ${user.lastName}`;
}

export function byId(items, id) {
  return items.find((item) => item.id === id);
}

export function getCourse(data, courseId) {
  return byId(data.courses, courseId);
}

export function getCourseVersion(data, versionId) {
  return byId(data.courseVersions, versionId);
}

export function getOwner(data, course) {
  return byId(data.users, course?.ownerId);
}

export function getModulesForVersion(data, courseVersionId) {
  return data.modules
    .filter((module) => module.courseVersionId === courseVersionId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export function getLessonsForModule(data, moduleId) {
  return data.lessons
    .filter((lesson) => lesson.moduleId === moduleId)
    .sort((a, b) => a.sortOrder - b.sortOrder);
}

export function getLessonsForVersion(data, courseVersionId) {
  const moduleIds = getModulesForVersion(data, courseVersionId).map((module) => module.id);
  return data.lessons
    .filter((lesson) => moduleIds.includes(lesson.moduleId))
    .sort((a, b) => {
      const moduleA = byId(data.modules, a.moduleId);
      const moduleB = byId(data.modules, b.moduleId);
      if (moduleA?.sortOrder !== moduleB?.sortOrder) return (moduleA?.sortOrder || 0) - (moduleB?.sortOrder || 0);
      return a.sortOrder - b.sortOrder;
    });
}

export function getQuizzesForVersion(data, courseVersionId) {
  return data.quizzes.filter((quiz) => quiz.courseVersionId === courseVersionId);
}

export function getExercisesForVersion(data, courseVersionId) {
  return (data.exercises || [])
    .filter((exercise) => exercise.courseVersionId === courseVersionId)
    .sort((a, b) => {
      const moduleA = byId(data.modules, a.moduleId);
      const moduleB = byId(data.modules, b.moduleId);
      if (moduleA?.sortOrder !== moduleB?.sortOrder) return (moduleA?.sortOrder || 0) - (moduleB?.sortOrder || 0);
      return (a.sortOrder || 0) - (b.sortOrder || 0);
    });
}

export function getExercisesForModule(data, courseVersionId, moduleId) {
  return getExercisesForVersion(data, courseVersionId)
    .filter((exercise) => exercise.moduleId === moduleId);
}

export function requiredItemCount(data, courseVersionId) {
  const lessons = getLessonsForVersion(data, courseVersionId).filter((lesson) => lesson.isRequired);
  const quizzes = getQuizzesForVersion(data, courseVersionId).filter((quiz) => quiz.isRequired);
  const exercises = getExercisesForVersion(data, courseVersionId).filter((exercise) => exercise.isRequired);
  return lessons.length + quizzes.length + exercises.length;
}

export function getLatestQuizAttempt(assignment, quizId) {
  const attempts = assignment.quizAttempts?.filter((attempt) => attempt.quizId === quizId) || [];
  return attempts.sort((a, b) => b.attemptNumber - a.attemptNumber)[0];
}

export function bestQuizScore(assignment, quizId) {
  const attempts = assignment.quizAttempts?.filter((attempt) => attempt.quizId === quizId) || [];
  if (!attempts.length) return null;
  return Math.max(...attempts.map((attempt) => Number(attempt.scorePercent || 0)));
}

export function quizPassed(assignment, quiz) {
  return (assignment.quizAttempts || []).some((attempt) => attempt.quizId === quiz.id && attempt.status === 'passed');
}

export function getExerciseSubmissions(assignment, exerciseId) {
  return assignment.exerciseSubmissions?.filter((submission) => submission.exerciseId === exerciseId) || [];
}

export function getLatestExerciseSubmission(assignment, exerciseId) {
  const submissions = getExerciseSubmissions(assignment, exerciseId);
  return submissions.sort((a, b) => Number(b.attemptNumber || 0) - Number(a.attemptNumber || 0))[0];
}

export function bestExerciseScore(assignment, exerciseId) {
  const reviewed = getExerciseSubmissions(assignment, exerciseId)
    .filter((submission) => submission.scorePercent !== undefined && submission.scorePercent !== null);
  if (!reviewed.length) return null;
  return Math.max(...reviewed.map((submission) => Number(submission.scorePercent || 0)));
}

export function exercisePassed(assignment, exercise) {
  return getExerciseSubmissions(assignment, exercise.id).some((submission) => submission.status === 'passed');
}

export function exerciseNeedsReview(assignment, exerciseId) {
  return getExerciseSubmissions(assignment, exerciseId).some((submission) => submission.status === 'needs_review');
}

export function getAssignmentProgress(data, assignment) {
  const requiredLessons = getLessonsForVersion(data, assignment.courseVersionId).filter((lesson) => lesson.isRequired);
  const requiredQuizzes = getQuizzesForVersion(data, assignment.courseVersionId).filter((quiz) => quiz.isRequired);
  const requiredExercises = getExercisesForVersion(data, assignment.courseVersionId).filter((exercise) => exercise.isRequired);
  const lessonCompleted = requiredLessons.filter((lesson) => assignment.lessonProgress?.[lesson.id]?.status === 'completed').length;
  const quizzesPassed = requiredQuizzes.filter((quiz) => quizPassed(assignment, quiz)).length;
  const exercisesPassed = requiredExercises.filter((exercise) => exercisePassed(assignment, exercise)).length;
  const total = requiredLessons.length + requiredQuizzes.length + requiredExercises.length;
  const completed = lessonCompleted + quizzesPassed + exercisesPassed;
  const percent = total ? Math.round((completed / total) * 100) : 0;
  const manualPending = requiredLessons.some((lesson) => assignment.lessonProgress?.[lesson.id]?.status === 'needs_review')
    || requiredExercises.some((exercise) => exerciseNeedsReview(assignment, exercise.id));
  return { percent, completed, total, manualPending, lessonCompleted, quizCompleted: quizzesPassed, exerciseCompleted: exercisesPassed };
}

export function isOverdue(assignment) {
  if (!assignment?.dueDate || assignment.status === 'completed' || assignment.status === 'revoked') return false;
  const due = new Date(`${assignment.dueDate}T23:59:59`);
  return due.getTime() < Date.now();
}

export function derivedAssignmentStatus(data, assignment) {
  if (!assignment) return 'not_started';
  if (assignment.status === 'revoked') return 'revoked';
  if (assignment.status === 'completed') return 'completed';
  const progress = getAssignmentProgress(data, assignment);
  if (progress.manualPending || assignment.status === 'needs_review') return 'needs_review';
  if (isOverdue(assignment)) return 'overdue';
  if (progress.completed > 0 || assignment.startedAt) return 'in_progress';
  return 'not_started';
}

export function getModuleEndQuizzes(data, courseVersionId, moduleId) {
  return getQuizzesForVersion(data, courseVersionId)
    .filter((quiz) => quiz.moduleId === moduleId)
    .filter((quiz) => !quiz.placement || quiz.placement === 'module_end' || quiz.placement === 'both');
}

export function getAfterLessonQuizzes(data, courseVersionId, lessonId) {
  return getQuizzesForVersion(data, courseVersionId)
    .filter((quiz) => quiz.afterLessonId === lessonId)
    .filter((quiz) => quiz.placement === 'after_lesson' || quiz.placement === 'both');
}


export function getQuizAvailability(data, assignment, quiz) {
  if (!assignment || !quiz) {
    return {
      canTake: false,
      type: 'quiz',
      message: 'Quiz not found or access removed.',
      incompleteLessons: []
    };
  }

  if (quiz.afterLessonId) {
    const lesson = byId(data.lessons, quiz.afterLessonId);
    const completed = assignment.lessonProgress?.[quiz.afterLessonId]?.status === 'completed';
    return {
      canTake: completed,
      type: 'lesson',
      lesson,
      targetTitle: lesson?.title || 'the attached lesson',
      incompleteLessons: completed || !lesson ? [] : [lesson],
      message: completed ? '' : `Complete the attached lesson, ${lesson?.title || 'the attached lesson'}, before taking this quiz.`
    };
  }

  if (quiz.moduleId) {
    const module = byId(data.modules, quiz.moduleId);
    const moduleLessons = getLessonsForModule(data, quiz.moduleId).filter((lesson) => lesson.isRequired !== false);
    const incompleteLessons = moduleLessons.filter((lesson) => assignment.lessonProgress?.[lesson.id]?.status !== 'completed');
    return {
      canTake: incompleteLessons.length === 0,
      type: 'module',
      module,
      targetTitle: module?.title || 'the attached module',
      incompleteLessons,
      message: incompleteLessons.length === 0 ? '' : `Complete all required lessons in ${module?.title || 'the attached module'} before taking this quiz.`
    };
  }

  return {
    canTake: true,
    type: 'quiz',
    message: '',
    incompleteLessons: []
  };
}

export function getLearningSequence(data, assignment) {
  if (!assignment) return [];
  const sequence = [];
  const checkedQuizIds = new Set();
  const modules = getModulesForVersion(data, assignment.courseVersionId);

  modules.forEach((module) => {
    getLessonsForModule(data, module.id).forEach((lesson) => {
      sequence.push({ type: 'lesson', id: lesson.id, moduleId: module.id, title: lesson.title });
      getAfterLessonQuizzes(data, assignment.courseVersionId, lesson.id).forEach((quiz) => {
        if (!checkedQuizIds.has(quiz.id)) {
          checkedQuizIds.add(quiz.id);
          sequence.push({ type: 'quiz', id: quiz.id, moduleId: module.id, afterLessonId: lesson.id, title: quiz.title });
        }
      });
    });

    getModuleEndQuizzes(data, assignment.courseVersionId, module.id).forEach((quiz) => {
      if (!checkedQuizIds.has(quiz.id)) {
        checkedQuizIds.add(quiz.id);
        sequence.push({ type: 'quiz', id: quiz.id, moduleId: module.id, title: quiz.title });
      }
    });

    getExercisesForModule(data, assignment.courseVersionId, module.id).forEach((exercise) => {
      sequence.push({ type: 'exercise', id: exercise.id, moduleId: module.id, title: exercise.title });
    });
  });

  return sequence;
}

export function getLearningNavigation(data, assignment, currentType, currentId) {
  const sequence = getLearningSequence(data, assignment);
  const index = sequence.findIndex((item) => item.type === currentType && item.id === currentId);
  const toPath = (item) => {
    if (!item || !assignment) return '';
    if (item.type === 'lesson') return `/learner/lesson/${assignment.id}:${item.id}`;
    if (item.type === 'exercise') return `/learner/exercise/${assignment.id}:${item.id}`;
    return `/learner/quiz/${assignment.id}:${item.id}`;
  };
  const previous = index > 0 ? sequence[index - 1] : null;
  const next = index >= 0 && index < sequence.length - 1 ? sequence[index + 1] : null;
  return {
    sequence,
    currentIndex: index,
    previous: previous ? { ...previous, path: toPath(previous) } : null,
    next: next ? { ...next, path: toPath(next) } : null
  };
}

export function getNextIncompleteItem(data, assignment) {
  const modules = getModulesForVersion(data, assignment.courseVersionId);
  const checkedQuizIds = new Set();

  for (const module of modules) {
    const lessons = getLessonsForModule(data, module.id);
    for (const lesson of lessons) {
      if (lesson.isRequired && assignment.lessonProgress?.[lesson.id]?.status !== 'completed') {
        return { type: 'lesson', id: lesson.id };
      }
      const afterLessonQuizzes = getAfterLessonQuizzes(data, assignment.courseVersionId, lesson.id).filter((quiz) => quiz.isRequired);
      for (const quiz of afterLessonQuizzes) {
        checkedQuizIds.add(quiz.id);
        if (!quizPassed(assignment, quiz)) {
          const gate = getQuizAvailability(data, assignment, quiz);
          if (!gate.canTake && gate.incompleteLessons?.[0]) {
            return { type: 'lesson', id: gate.incompleteLessons[0].id };
          }
          return { type: 'quiz', id: quiz.id };
        }
      }
    }

    const moduleEndQuizzes = getModuleEndQuizzes(data, assignment.courseVersionId, module.id).filter((quiz) => quiz.isRequired);
    for (const quiz of moduleEndQuizzes) {
      if (checkedQuizIds.has(quiz.id) && quiz.placement === 'both') continue;
      checkedQuizIds.add(quiz.id);
      if (!quizPassed(assignment, quiz)) {
        const gate = getQuizAvailability(data, assignment, quiz);
        if (!gate.canTake && gate.incompleteLessons?.[0]) {
          return { type: 'lesson', id: gate.incompleteLessons[0].id };
        }
        return { type: 'quiz', id: quiz.id };
      }
    }

    const moduleExercises = getExercisesForModule(data, assignment.courseVersionId, module.id).filter((exercise) => exercise.isRequired);
    for (const exercise of moduleExercises) {
      if (!exercisePassed(assignment, exercise)) {
        return { type: 'exercise', id: exercise.id };
      }
    }
  }

  const remainingQuizzes = getQuizzesForVersion(data, assignment.courseVersionId)
    .filter((quiz) => quiz.isRequired && !checkedQuizIds.has(quiz.id));
  for (const quiz of remainingQuizzes) {
    if (!quizPassed(assignment, quiz)) {
      const gate = getQuizAvailability(data, assignment, quiz);
      if (!gate.canTake && gate.incompleteLessons?.[0]) {
        return { type: 'lesson', id: gate.incompleteLessons[0].id };
      }
      return { type: 'quiz', id: quiz.id };
    }
  }
  return null;
}

export function assignmentGrade(data, assignment) {
  const quizzes = getQuizzesForVersion(data, assignment.courseVersionId).filter((quiz) => quiz.isRequired);
  const exercises = getExercisesForVersion(data, assignment.courseVersionId).filter((exercise) => exercise.isRequired);
  const scores = [
    ...quizzes.map((quiz) => bestQuizScore(assignment, quiz.id)),
    ...exercises.map((exercise) => bestExerciseScore(assignment, exercise.id))
  ].filter((score) => score !== null);
  if (!scores.length) return null;
  return Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length);
}

export function getCourseTrainers(data, course) {
  const ids = new Set(course?.trainerIds || []);
  if (course?.ownerId) ids.add(course.ownerId);
  return data.users.filter((user) => ids.has(user.id));
}

export function trainerNames(data, course) {
  const trainers = getCourseTrainers(data, course).filter(Boolean);
  return trainers.length ? trainers.map(fullName).join(', ') : 'No trainer assigned';
}

export function userIsCourseTrainer(data, course, userId) {
  if (!course || !userId) return false;
  return course.ownerId === userId || (course.trainerIds || []).includes(userId);
}

export function getProgramCourses(data, programId) {
  return (data.programCourses || [])
    .filter((item) => item.programId === programId)
    .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0))
    .map((item) => ({ ...item, course: getCourse(data, item.courseId) }))
    .filter((item) => item.course);
}

export function getProgramAssignmentsForUser(data, userId) {
  return (data.programAssignments || [])
    .filter((assignment) => assignment.assignedToUserId === userId && assignment.status !== 'revoked');
}

export function getAssignmentsForProgramAssignment(data, programAssignment) {
  if (!programAssignment) return [];
  return (data.assignments || [])
    .filter((assignment) => assignment.programAssignmentId === programAssignment.id && assignment.status !== 'revoked');
}

export function getProgramProgress(data, programAssignment) {
  const courseAssignments = getAssignmentsForProgramAssignment(data, programAssignment);
  const total = courseAssignments.length;
  const completed = courseAssignments.filter((assignment) => derivedAssignmentStatus(data, assignment) === 'completed').length;
  const needsReview = courseAssignments.some((assignment) => derivedAssignmentStatus(data, assignment) === 'needs_review');
  const overdue = courseAssignments.some((assignment) => derivedAssignmentStatus(data, assignment) === 'overdue');
  const inProgress = courseAssignments.some((assignment) => derivedAssignmentStatus(data, assignment) === 'in_progress');
  const percent = total ? Math.round((completed / total) * 100) : 0;
  let status = 'not_started';
  if (total && completed === total) status = 'completed';
  else if (needsReview) status = 'needs_review';
  else if (overdue) status = 'overdue';
  else if (inProgress || completed > 0) status = 'in_progress';
  return { total, completed, percent, status };
}

export function programGrade(data, programAssignment) {
  const grades = getAssignmentsForProgramAssignment(data, programAssignment)
    .map((assignment) => assignmentGrade(data, assignment))
    .filter((grade) => grade !== null && grade !== undefined);
  if (!grades.length) return null;
  return Math.round(grades.reduce((sum, grade) => sum + Number(grade || 0), 0) / grades.length);
}

export function statusLabel(value) {
  const map = {
    admin: 'Admin',
    trainer: 'Trainer',
    learner: 'Learner',
    active: 'Active',
    inactive: 'Inactive',
    invited: 'Invited',
    suspended: 'Suspended',
    draft: 'Draft',
    in_review: 'In Review',
    changes_requested: 'Changes Requested',
    published: 'Published',
    archived: 'Archived',
    not_started: 'Not Started',
    in_progress: 'In Progress',
    completed: 'Completed',
    overdue: 'Overdue',
    needs_review: 'Needs Review',
    attendance_pending: 'Attendance Pending',
    revision_requested: 'Revision Requested',
    revoked: 'Revoked',
    passed: 'Passed',
    failed: 'Failed',
    active_certificate: 'Active'
  };
  return map[value] || String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function makeId(prefix) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}-${Date.now().toString(36)}`;
}

export function certificateNumber(course) {
  const categoryCode = (course?.category || 'LMS')
    .split(' ')
    .map((word) => word[0])
    .join('')
    .slice(0, 4)
    .toUpperCase();
  return `LMS-${categoryCode}-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 999999)).padStart(6, '0')}`;
}

export function verificationCode() {
  return `VERIFY-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
}

export function completionSnapshot(data, assignment) {
  const progress = getAssignmentProgress(data, assignment);
  const grade = assignmentGrade(data, assignment);
  return { ...progress, grade, status: derivedAssignmentStatus(data, assignment) };
}
